import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Switch,
  Button,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  ImageOutlined,
  AnalyticsOutlined,
  EventOutlined,
  PersonAddOutlined,
  LockOutlined,
  MenuBookOutlined,
  LinkOutlined,
  DashboardOutlined,
  SchoolOutlined,
  SettingsSuggestOutlined,
  EmailOutlined,
  OpenInNew,
} from "@mui/icons-material";
import TrackChangesOutlined from "@mui/icons-material/TrackChangesOutlined";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LoadingButton } from "@mui/lab";
import LanguageIcon from "@mui/icons-material/Language";
import axios from "axios";
import { toast } from "react-toastify";

import MyQuill from "../quill/MyQuill";
import { ADMIN, API_ENDPOINT } from "../utils/constants";
import { getHeader, toastError, validateEmail } from "../utils/util";
import useGetEventSettings from "../hooks/useGetEventSettings";
import useCatalogueFormStatus from "../hooks/useCatalogueFormStatus";

const SettingsPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useGetEventSettings();

  // Kept in case you rely on it elsewhere, but removed from the settings population logic
  const { data: cFormData } = useCatalogueFormStatus();

  const [senderEmail, setSenderEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [settings, setSettings] = useState({
    eventName: "",
    eventStartDate: null,
    eventEndDate: null,
    visitorRegStartDate: null,
    visitorRegEndDate: null,
    tdStartDate: null, // Added Trade Delegation Start Date
    tdEndDate: null, // Added Trade Delegation End Date
    disableVisitorReg: false,
    disableExhibitionCatalogue: false,
    visitorRegMessage: "",
    exhibitionCatalogueMessage: "",
    userDashLink: "",
    visitorRegLink: "",
    studentRegLink: "",
  });

  // Consolidated useEffect: Only depends on 'data' to populate settings
  useEffect(() => {
    if (data) {
      if (data.emailTemplate?.senderEmail) {
        setSenderEmail(data.emailTemplate.senderEmail);
      }

      setSettings((prev) => ({
        ...prev,
        eventName: data?.eventName ?? "",
        eventStartDate: data.eventStartDate
          ? new Date(data.eventStartDate)
          : null,
        eventEndDate: data.eventEndDate ? new Date(data.eventEndDate) : null,
        visitorRegStartDate: data.visitorRegStartDate
          ? new Date(data.visitorRegStartDate)
          : null,
        visitorRegEndDate: data.visitorRegEndDate
          ? new Date(data.visitorRegEndDate)
          : null,
        tdStartDate: data.tdStartDate ? new Date(data.tdStartDate) : null, // Populate TD Start Date
        tdEndDate: data.tdEndDate ? new Date(data.tdEndDate) : null, // Populate TD End Date
        disableVisitorReg: data?.disableVisitorReg === true,
        disableExhibitionCatalogue:
          data?.disableExhibitionCatalogue === "true" ||
          data?.disableExhibitionCatalogue === true,
        visitorRegMessage: data?.visitorRegMessage ?? "",
        exhibitionCatalogueMessage: data?.exhibitionCatalogueMessage ?? "",
        userDashLink: data?.userDashLink ?? "",
        visitorRegLink: data?.visitorRegLink ?? "",
        studentRegLink: data?.studentRegLink ?? "",
      }));
    }
  }, [data]);

  const handleDateChange = (field) => (date) =>
    setSettings((prev) => ({ ...prev, [field]: date }));
  const handleSwitchChange = (field) => (event) =>
    setSettings((prev) => ({ ...prev, [field]: event.target.checked }));
  const handleMessageChange = (field) => (newValue) =>
    setSettings((prev) => ({ ...prev, [field]: newValue }));
  const handleLinkChange = (field) => (event) =>
    setSettings((prev) => ({ ...prev, [field]: event.target.value }));

  const validateSettings = () => {
    if (settings.disableVisitorReg && !settings.visitorRegMessage.trim()) {
      toast.warning(
        "Please provide a message for disabled visitor registration"
      );
      return false;
    }
    if (
      settings.disableExhibitionCatalogue &&
      !settings.exhibitionCatalogueMessage.trim()
    ) {
      toast.warning(
        "Please provide a message for disabled exhibition catalogue"
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateSettings()) return;
    try {
      setSavingSettings(true);
      const formattedSettings = {
        ...settings,
        eventStartDate: settings.eventStartDate
          ? settings.eventStartDate.toISOString()
          : null,
        eventEndDate: settings.eventEndDate
          ? settings.eventEndDate.toISOString()
          : null,
        visitorRegStartDate: settings.visitorRegStartDate
          ? settings.visitorRegStartDate.toISOString()
          : null,
        visitorRegEndDate: settings.visitorRegEndDate
          ? settings.visitorRegEndDate.toISOString()
          : null,
        tdStartDate: settings.tdStartDate
          ? settings.tdStartDate.toISOString()
          : null, // Format TD Start Date
        tdEndDate: settings.tdEndDate ? settings.tdEndDate.toISOString() : null, // Format TD End Date
      };

      const { data: responseData } = await axios.put(
        `${API_ENDPOINT}admin/save-settings`,
        formattedSettings,
        getHeader(ADMIN)
      );
      toast.success(responseData.message || "Settings saved successfully");
    } catch (error) {
      toastError(error);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleEmailSubmit = async () => {
    try {
      setSavingEmail(true);
      const { data: responseData } = await axios.post(
        `${API_ENDPOINT}admin/update-sender-email`,
        { email: senderEmail },
        getHeader(ADMIN)
      );
      refetch();
      toast.success(responseData.message || "Email updated successfully");
    } catch (error) {
      toastError(error);
    } finally {
      setSavingEmail(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        fontWeight="bold"
        sx={{ mb: 4, display: "flex", alignItems: "center", gap: 1 }}
      >
        <SettingsSuggestOutlined fontSize="large" color="primary" />
        Application Settings
      </Typography>

      <Grid container spacing={4}>
        {/* Left Column: Quick Actions & Links */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardHeader
              title="Quick Actions"
              titleTypographyProps={{ variant: "h6" }}
            />
            <Divider />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ImageOutlined />}
                onClick={() => navigate("/home/banner-logo-settings")}
                sx={{ justifyContent: "flex-start" }}
              >
                Banner & Logo
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LanguageIcon />}
                onClick={() => navigate("/home/meta-data-editor")}
                sx={{ justifyContent: "flex-start" }}
              >
                App Site Metadata
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AnalyticsOutlined />}
                onClick={() => navigate("/home/update-gtag")}
                sx={{ justifyContent: "flex-start" }}
              >
                Gtag Analytics
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AnalyticsOutlined />}
                onClick={() => navigate("/home/update-meta-pixel-tag")}
                sx={{ justifyContent: "flex-start" }}
              >
                Meta Pixel Analytics
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<TrackChangesOutlined />}
                onClick={() => navigate("/home/registration-analytics-tracker")}
                sx={{ justifyContent: "flex-start" }}
              >
                Registration Tracker
              </Button>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardHeader
              title="Email Configuration"
              titleTypographyProps={{ variant: "h6" }}
              avatar={<EmailOutlined color="action" />}
            />
            <Divider />
            <CardContent>
              <TextField
                fullWidth
                value={senderEmail}
                error={Boolean(senderEmail && !validateEmail(senderEmail))}
                helperText={
                  senderEmail && !validateEmail(senderEmail)
                    ? "Invalid email address"
                    : ""
                }
                onChange={(e) => setSenderEmail(e.target.value)}
                variant="outlined"
                size="small"
                label="Global Sender Email"
                sx={{ mb: 2 }}
              />
              <LoadingButton
                fullWidth
                loading={savingEmail}
                onClick={handleEmailSubmit}
                variant="contained"
                color="secondary"
                disabled={!senderEmail || !validateEmail(senderEmail)}
              >
                Update Email
              </LoadingButton>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Main Settings Form */}
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardHeader
              title="Event Details"
              titleTypographyProps={{ variant: "h6" }}
              avatar={<EventOutlined color="action" />}
            />
            <Divider />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <TextField
                fullWidth
                size="small"
                label="Event Name"
                placeholder="e.g. Tech Expo 2026"
                value={settings.eventName}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    eventName: e.target.value,
                  }))
                }
                variant="outlined"
              />

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <DatePicker
                    label="Event Start Date"
                    value={settings.eventStartDate}
                    onChange={handleDateChange("eventStartDate")}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                  <DatePicker
                    label="Event End Date"
                    value={settings.eventEndDate}
                    onChange={handleDateChange("eventEndDate")}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ mb: 4 }}>
            <CardHeader
              title="Registration & Catalogue Rules"
              titleTypographyProps={{ variant: "h6" }}
              avatar={<LockOutlined color="action" />}
            />
            <Divider />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <DatePicker
                    label="Visitor Reg. Start"
                    value={settings.visitorRegStartDate}
                    onChange={handleDateChange("visitorRegStartDate")}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                  <DatePicker
                    label="Visitor Reg. End"
                    value={settings.visitorRegEndDate}
                    onChange={handleDateChange("visitorRegEndDate")}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </Box>

                {/* Added Trade Delegation Dates */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  }}
                >
                  <DatePicker
                    label="Trade Delegation Start"
                    value={settings.tdStartDate}
                    onChange={handleDateChange("tdStartDate")}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                  <DatePicker
                    label="Trade Delegation End"
                    value={settings.tdEndDate}
                    onChange={handleDateChange("tdEndDate")}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </Box>
              </LocalizationProvider>

              <Box
                sx={{ p: 2, bgcolor: "background.default", borderRadius: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: settings.disableVisitorReg ? 2 : 0,
                  }}
                >
                  <PersonAddOutlined sx={{ mr: 2, color: "text.secondary" }} />
                  <Typography
                    variant="body1"
                    sx={{ flexGrow: 1, fontWeight: 500 }}
                  >
                    Disable Visitor Registration
                  </Typography>
                  <Switch
                    checked={settings.disableVisitorReg}
                    onChange={handleSwitchChange("disableVisitorReg")}
                    color="warning"
                  />
                </Box>
                {settings.disableVisitorReg && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      gutterBottom
                    >
                      Message to display to users:
                    </Typography>
                    <MyQuill
                      value={settings.visitorRegMessage}
                      onChange={handleMessageChange("visitorRegMessage")}
                    />
                  </Box>
                )}
              </Box>

              <Box
                sx={{ p: 2, bgcolor: "background.default", borderRadius: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: settings.disableExhibitionCatalogue ? 2 : 0,
                  }}
                >
                  <MenuBookOutlined sx={{ mr: 2, color: "text.secondary" }} />
                  <Typography
                    variant="body1"
                    sx={{ flexGrow: 1, fontWeight: 500 }}
                  >
                    Disable Exhibition Catalogue
                  </Typography>
                  <Switch
                    checked={settings.disableExhibitionCatalogue}
                    onChange={handleSwitchChange("disableExhibitionCatalogue")}
                    color="warning"
                  />
                </Box>
                {settings.disableExhibitionCatalogue && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      gutterBottom
                    >
                      Message to display to users:
                    </Typography>
                    <MyQuill
                      value={settings.exhibitionCatalogueMessage}
                      onChange={handleMessageChange(
                        "exhibitionCatalogueMessage"
                      )}
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardHeader
              title="External Routing Links"
              titleTypographyProps={{ variant: "h6" }}
              avatar={<LinkOutlined color="action" />}
            />
            <Divider />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              {/* User Dashboard Link */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="User Dashboard Link"
                  value={settings.userDashLink}
                  onChange={handleLinkChange("userDashLink")}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <DashboardOutlined
                        sx={{ mr: 1.5, color: "action.active" }}
                      />
                    ),
                    endAdornment: settings.userDashLink ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() =>
                            window.open(
                              settings.userDashLink,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          aria-label="open user dashboard link"
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>

              {/* Visitor Registration Link */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Visitor Registration Link"
                  value={settings.visitorRegLink}
                  onChange={handleLinkChange("visitorRegLink")}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <PersonAddOutlined
                        sx={{ mr: 1.5, color: "action.active" }}
                      />
                    ),
                    endAdornment: settings.visitorRegLink ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() =>
                            window.open(
                              settings.visitorRegLink,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          aria-label="open visitor registration link"
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>

              {/* Student Registration Link */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Student Registration Link"
                  value={settings.studentRegLink}
                  onChange={handleLinkChange("studentRegLink")}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <SchoolOutlined
                        sx={{ mr: 1.5, color: "action.active" }}
                      />
                    ),
                    endAdornment: settings.studentRegLink ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() =>
                            window.open(
                              settings.studentRegLink,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          aria-label="open student registration link"
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              loading={savingSettings}
              sx={{ px: 4, py: 1.5 }}
            >
              Save Core Settings
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SettingsPage;
